/*
 * IconButton: a square button with one icon and a required accessible label (also shown as a tooltip).
 *   <IconButton label={t('actions.copy')} icon={<Copy />} onClick={copy} />
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'
import { Tooltip } from './Tooltip'

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  label: string
  icon: ReactNode
  variant?: 'ghost' | 'secondary' | 'primary' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  /** show the label as a tooltip (default true) */
  tooltip?: boolean
}

const VARIANT = {
  ghost: 'text-muted hover:bg-control hover:text-ink active:bg-control-strong',
  secondary:
    'bg-surface text-body shadow-sm ring-1 ring-hairline-strong ring-inset hover:bg-surface-soft hover:text-ink',
  primary: 'bg-brand-600 text-on-brand shadow-button-primary hover:bg-brand-700',
  danger: 'text-danger-600 hover:bg-danger-50',
}
const SIZE = {
  sm: 'size-8 rounded-[10px] [&_svg]:size-4',
  md: 'size-10 rounded-xl [&_svg]:size-[18px]',
  lg: 'size-12 rounded-xl [&_svg]:size-5',
}

export const IconButton = forwardRef<HTMLButtonElement, Props>(function IconButton(
  {
    label,
    icon,
    variant = 'ghost',
    size = 'md',
    loading,
    tooltip = true,
    className,
    type,
    disabled,
    ...rest
  },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center justify-center transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 touch:min-h-11 touch:min-w-11',
        SIZE[size],
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
    </button>
  )
  return tooltip ? <Tooltip content={label}>{button}</Tooltip> : button
})
